import { useMemo, useEffect, lazy, Suspense } from "react";

import Header from "@/components/Header";
// Lazy load Footer - it's below the fold and doesn't affect LCP
const Footer = lazy(() => import("@/components/Footer"));
import { PageHead } from "@/components/PageHead";
import { PrerenderData } from "@/lib/prerenderData";
import { orderSections, renderSection } from "@/components/FlexSectionRenderer";
import { useFlexiblePageContent } from "@/hooks/useFlexiblePage";
import MenuPreloadScripts from "@/components/MenuPreloadScripts";
import { fetchHomePageContent, getCachedHomePageContent } from "@/lib/homepage";
import { usePageSeo } from "@/hooks/use-wp-seo";

type FlexiblePageProps = {
  slugs: string[];
  prerenderId: string;
  fallbackTitle: string;
  lang?: string;
  headTitle?: string;
  loadingLabel?: string;
  emptyLabel?: string;
  errorLabel?: string;
};

export function FlexiblePage({
  slugs,
  prerenderId,
  fallbackTitle,
  lang,
  headTitle,
  loadingLabel = "Loading page…",
  emptyLabel = "Sections not configured yet.",
  errorLabel = "Unable to load content right now.",
}: FlexiblePageProps) {
  const { content, loading, error, preview } = useFlexiblePageContent({ slugs, prerenderId, lang });

  // Fetch AIOSEO data for the page
  const { data: seoData } = usePageSeo(slugs, { lang, enabled: slugs.length > 0 });

  const sections = useMemo(() => orderSections(content?.sections ?? []), [content]);
  const hasSections = sections.length > 0;
  const pageTitle = headTitle ?? content?.title ?? fallbackTitle;

  // Preload menu data to prevent header flash on detail pages (same as homepage)
  // Only needed for non-prerendered pages (prerendered pages have menu data embedded in HTML)
  useEffect(() => {
    // Check if we're on a prerendered page by looking for embedded menu data
    const hasPrerenderedMenus = typeof window !== "undefined" && window.__PRERENDER_MENUS__;

    if (!hasPrerenderedMenus) {
      // Not a prerendered page, fetch homepage data for menus
      // Only fetch the menu for the current language to avoid conflicts
      const currentLang = lang || undefined;
      if (!getCachedHomePageContent(currentLang)) {
        fetchHomePageContent(currentLang ? { lang: currentLang } : undefined).catch(() => {});
      }
    }
  }, [lang]);

  return (
    <div className="min-h-screen bg-onplexus-dark text-white">
      <PageHead title={pageTitle} seo={seoData} />
      <MenuPreloadScripts />
      <Header />

      <main className="flex flex-col items-center gap-16 px-4 pb-24 pt-10 sm:px-6 md:px-8">
        {!preview && content && hasSections && <PrerenderData id={prerenderId} value={content} />}

        {loading && !content && (
          <div className="py-24 text-center text-white/70">{loadingLabel}</div>
        )}

        {!loading && error && <div className="py-24 text-center text-red-300">{errorLabel}</div>}

        {content && !error && hasSections && sections.map((section, index) => renderSection(section, index))}

        {content && !error && !hasSections && (
          <div className="py-24 text-center text-white/50">{emptyLabel}</div>
        )}
      </main>

      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  );
}
